import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { attendanceAPI, employeeAPI, settingsAPI } from '../../lib/api';
import { getLocation, getIP, checkGeofence, formatDistance } from '../../lib/location';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, MapPin, Globe, Clock, History, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const MyAttendance = () => {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [isRegularizeOpen, setIsRegularizeOpen] = useState(false);
    const [regData, setRegData] = useState({
        attendance_id: '',
        attendance_date: '',
        reason: '',
        check_in_time: '',
        check_out_time: '',
    });
    const [allowedLocations, setAllowedLocations] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [myConfig, setMyConfig] = useState(null);
    const [punchModal, setPunchModal] = useState({
        isOpen: false,
        state: 'fetching', // 'fetching', 'success', 'error'
        message: '',
        type: 'in' // 'in' or 'out'
    });

    useEffect(() => {
        fetchData();
    }, [selectedMonth]);

    const [regularizableDates, setRegularizableDates] = useState([]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
            const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0);
            
            const [statusRes, historyRes, empRes, locRes, configRes] = await Promise.all([
                attendanceAPI.getStatus(),
                attendanceAPI.getHistory({ 
                    employee_id: user.employee_id,
                    start_date: format(startOfMonth, 'yyyy-MM-dd'),
                    end_date: format(endOfMonth, 'yyyy-MM-dd')
                }),
                employeeAPI.getEmployee(user.employee_id),
                settingsAPI.getLocations(),
                attendanceAPI.getMyConfig()
            ]);
            setStatus(statusRes.data);
            setHistory(historyRes.data);
            console.log('Attendance History:', historyRes.data);
            setMyConfig(configRes.data);

            // Generate regularizable dates (All workdays in the selected month)
            const regDates = [];
            const daysInMonth = endOfMonth.getDate();
            
            for (let i = 1; i <= daysInMonth; i++) {
                const d = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), i);
                // Skip future dates
                if (d > new Date()) continue;
                
                // Use configured working days if available, otherwise fallback to Mon-Fri (1-5)
                const workingDays = configRes.data?.working_days || [1, 2, 3, 4, 5];
                if (workingDays.includes(d.getDay())) {
                    const dateStr = format(d, 'yyyy-MM-dd');
                    const histRecord = historyRes.data.find(h => h.date === dateStr);
                    
                    if (histRecord) {
                        regDates.push({
                            id: histRecord.id,
                            date: histRecord.date,
                            status: histRecord.status,
                            check_in: histRecord.check_in,
                            check_out: histRecord.check_out,
                            label: `${format(d, 'EEE, MMM dd')} (${histRecord.status})`
                        });
                    } else {
                        regDates.push({
                            id: null,
                            date: dateStr,
                            status: 'MISSING',
                            label: `${format(d, 'EEE, MMM dd')} (Missing)`
                        });
                    }
                }
            }

            setRegularizableDates(regDates.sort((a, b) => new Date(b.date) - new Date(a.date)));

            // Filter settings locations by employee's assigned work_locations
            const assignedNames = empRes.data.work_locations || [];
            const filtered = locRes.data.filter(l => assignedNames.includes(l.name));
            setAllowedLocations(filtered);
        } catch (error) {
            toast.error('Failed to fetch attendance data');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckIn = async () => {
        console.log('handleCheckIn called');
        setPunchModal(prev => ({ ...prev, isOpen: true, state: 'fetching', message: 'Fetching your location...', type: 'in' }));
        setActionLoading(true);
        try {
            console.log('Getting location...');
            const location = await getLocation();
            console.log('Location received:', location);
            const ip = await getIP();

            setPunchModal(prev => ({ ...prev, message: 'Validating geofence...' }));

            // Geofence check
            if (allowedLocations.length === 0) {
                const errorMsg = 'You are not tagged to any work location. Please contact your administrator.';
                setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'in' });
                setActionLoading(false);
                return;
            } else {
                const geoCheck = checkGeofence(location, allowedLocations);
                if (!geoCheck.allowed) {
                    const errorMsg = `You are outside the permitted work area. Nearest location: ${geoCheck.nearestLocation} (${formatDistance(geoCheck.distance)} away)`;
                    setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'in' });
                    setActionLoading(false);
                    return;
                }
            }

            setPunchModal(prev => ({ ...prev, message: 'Recording attendance...' }));

            await attendanceAPI.checkIn({
                ...location,
                ip_address: ip
            });
            
            setPunchModal({ isOpen: true, state: 'success', message: 'Successfully checked in!', type: 'in' });
            fetchData();
        } catch (error) {
            const errorMsg = error.message || 'Failed to check in. Please ensure location is enabled.';
            setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'in' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleCheckOut = async () => {
        console.log('handleCheckOut called');
        setPunchModal(prev => ({ ...prev, isOpen: true, state: 'fetching', message: 'Fetching your location...', type: 'out' }));
        setActionLoading(true);
        try {
            console.log('Getting location...');
            const location = await getLocation();
            console.log('Location received:', location);
            const ip = await getIP();

            setPunchModal(prev => ({ ...prev, message: 'Validating geofence...' }));

            // Geofence check
            if (allowedLocations.length === 0) {
                const errorMsg = 'You are not tagged to any work location. Please contact your administrator.';
                setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'out' });
                setActionLoading(false);
                return;
            } else {
                const geoCheck = checkGeofence(location, allowedLocations);
                if (!geoCheck.allowed) {
                    const errorMsg = `You are outside the permitted work area. Nearest location: ${geoCheck.nearestLocation} (${formatDistance(geoCheck.distance)} away)`;
                    setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'out' });
                    setActionLoading(false);
                    return;
                }
            }

            setPunchModal(prev => ({ ...prev, message: 'Recording attendance...' }));

            await attendanceAPI.checkOut({
                ...location,
                ip_address: ip
            });
            
            setPunchModal({ isOpen: true, state: 'success', message: 'Successfully checked out!', type: 'out' });
            fetchData();
        } catch (error) {
            const errorMsg = error.message || 'Failed to check out. Please ensure location is enabled.';
            setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: 'out' });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegularizeSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const dateStr = regData.attendance_date || regularizableDates.find(d => d.id === regData.attendance_id)?.date;
            
            await attendanceAPI.regularize({
                attendance_id: regData.attendance_id || null,
                attendance_date: regData.attendance_date || null,
                reason: regData.reason,
                requested_check_in: regData.check_in_time ? new Date(`${dateStr}T${regData.check_in_time}`).toISOString() : null,
                requested_check_out: regData.check_out_time ? new Date(`${dateStr}T${regData.check_out_time}`).toISOString() : null,
            });
            toast.success('Regularization request submitted');
            setIsRegularizeOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit request');
        } finally {
            setActionLoading(false);
        }
    };

    const statusColors = {
        PRESENT: 'bg-green-100 text-green-800',
        ABSENT: 'bg-red-100 text-red-800',
        LATE: 'bg-yellow-100 text-yellow-800',
        HALF_DAY: 'bg-orange-100 text-orange-800',
        ON_LEAVE: 'bg-blue-100 text-blue-800',
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            {/* Punch Status Modal */}
            <Dialog open={punchModal.isOpen} onOpenChange={(open) => !actionLoading && setPunchModal(prev => ({ ...prev, isOpen: open }))}>
                <DialogContent className="sm:max-w-[400px] text-center py-10 z-[100]">
                    <div className="flex flex-col items-center justify-center space-y-6">
                        {punchModal.state === 'fetching' ? (
                            <>
                                <div className="relative">
                                    <div className="h-20 w-20 rounded-full border-4 border-primary/20 animate-pulse" />
                                    <Loader2 className="h-10 w-10 text-primary animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                </div>
                                <div className="space-y-2">
                                    <DialogTitle className="text-xl">Processing Punch {punchModal.type === 'in' ? 'In' : 'Out'}</DialogTitle>
                                    <DialogDescription className="text-base">{punchModal.message}</DialogDescription>
                                </div>
                            </>
                        ) : punchModal.state === 'success' ? (
                            <>
                                <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center">
                                    <CheckCircle className="h-12 w-12 text-green-500" />
                                </div>
                                <div className="space-y-2">
                                    <DialogTitle className="text-xl text-green-700">Punch Successful!</DialogTitle>
                                    <DialogDescription className="text-base text-green-600/80">{punchModal.message}</DialogDescription>
                                </div>
                                <Button 
                                    className="w-full bg-green-600 hover:bg-green-700" 
                                    onClick={() => setPunchModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Dismiss
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center">
                                    <XCircle className="h-12 w-12 text-red-500" />
                                </div>
                                <div className="space-y-2">
                                    <DialogTitle className="text-xl text-red-700">Punch Failed</DialogTitle>
                                    <DialogDescription className="text-base text-red-600/80">{punchModal.message}</DialogDescription>
                                </div>
                                <Button 
                                    variant="outline"
                                    className="w-full border-red-200 text-red-700 hover:bg-red-50" 
                                    onClick={() => setPunchModal(prev => ({ ...prev, isOpen: false }))}
                                >
                                    Try Again
                                </Button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
                    <p className="text-muted-foreground">Track your work hours and attendance history</p>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Punch In/Out Card */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5" />
                            Daily Punch
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col items-center justify-center py-6 space-y-6">
                            <div className="text-center">
                                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Today's Date</p>
                                <p className="text-2xl font-bold">{format(new Date(), 'EEEE, MMMM do')}</p>
                            </div>

                            <div className="flex gap-4">
                                {!status?.check_in ? (
                                    <Button
                                        size="lg"
                                        className="h-32 w-32 rounded-full text-xl font-bold bg-green-600 hover:bg-green-700 shadow-lg shadow-green-100"
                                        onClick={handleCheckIn}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? <Loader2 className="animate-spin" /> : 'Punch In'}
                                    </Button>
                                ) : !status?.check_out ? (
                                    <Button
                                        size="lg"
                                        className="h-32 w-32 rounded-full text-xl font-bold bg-orange-600 hover:bg-orange-700 shadow-lg shadow-orange-100"
                                        onClick={handleCheckOut}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? <Loader2 className="animate-spin" /> : 'Punch Out'}
                                    </Button>
                                ) : (
                                    <div className="h-32 w-32 rounded-full flex flex-col items-center justify-center bg-gray-100 text-gray-500 border-2 border-dashed">
                                        <History className="h-8 w-8 mb-1" />
                                        <span className="text-xs font-bold uppercase">Done</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-8 w-full max-w-sm pt-4">
                                <div className="text-center border-r">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 uppercase">Punch In</p>
                                    {status?.check_in ? (
                                        <p className="text-xl font-semibold">{format(new Date(status.check_in), 'hh:mm a')}</p>
                                    ) : (
                                        <p className="text-xl font-semibold text-gray-300">--:--</p>
                                    )}
                                </div>
                                <div className="text-center">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 uppercase">Punch Out</p>
                                    {status?.check_out ? (
                                        <p className="text-xl font-semibold">{format(new Date(status.check_out), 'hh:mm a')}</p>
                                    ) : (
                                        <p className="text-xl font-semibold text-gray-300">--:--</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Current Info Card */}
                <Card>
                    <CardHeader>
                        <CardTitle>Current Session</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-start gap-3">
                            <Globe className="h-5 w-5 mt-0.5 text-blue-500" />
                            <div>
                                <p className="text-sm font-medium">IP Address</p>
                                <p className="text-sm text-muted-foreground">{status?.check_in_ip || 'Not recorded'}</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin className="h-5 w-5 mt-0.5 text-red-500" />
                            <div>
                                <p className="text-sm font-medium">Location</p>
                                {status?.check_in_location ? (
                                    <p className="text-sm text-muted-foreground animate-pulse">
                                        {status.check_in_location.address || `${status.check_in_location.latitude.toFixed(4)}, ${status.check_in_location.longitude.toFixed(4)}`}
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Not recorded</p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-start gap-3 pt-2">
                            <AlertCircle className="h-5 w-5 mt-0.5 text-orange-500" />
                            <div>
                                <p className="text-sm font-medium">Status</p>
                                <Badge className={statusColors[status?.status || 'PRESENT']}>
                                    {status?.status || 'NOT PUNCHED'}
                                </Badge>
                            </div>
                        </div>

                        {allowedLocations.length > 0 ? (
                            <div className="pt-4 border-t mt-4">
                                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Assigned Work Areas</p>
                                <div className="space-y-2">
                                    {allowedLocations.map(loc => (
                                        <div key={loc.id} className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <div className="h-2 w-2 rounded-full bg-green-500" />
                                                <span>{loc.name}</span>
                                            </div>
                                            <span className="text-muted-foreground text-xs">{Math.min(loc.radius || 100, 50)}m radius</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-100">
                                    <MapPin className="h-3 w-3" />
                                    <span>Geofencing is active for your account</span>
                                </div>
                            </div>
                        ) : (
                            <div className="pt-4 border-t mt-4">
                                <div className="mt-3 flex items-start gap-2 text-xs text-red-600 bg-red-50 p-3 rounded border border-red-100">
                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                    <span>
                                        <strong>No Location Tagged:</strong> You are not tagged to any work location. You will not be able to mark attendance. Please contact your administrator.
                                    </span>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* History Card */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-4">
                        <CardTitle>History</CardTitle>
                        <div className="flex items-center gap-2">
                            <Select 
                                value={selectedMonth.getMonth().toString()} 
                                onValueChange={(val) => setSelectedMonth(new Date(selectedMonth.getFullYear(), parseInt(val), 1))}
                            >
                                <SelectTrigger className="w-[140px] h-9">
                                    <SelectValue placeholder="Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <SelectItem key={i} value={i.toString()}>
                                            {format(new Date(2000, i, 1), 'MMMM')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select 
                                value={selectedMonth.getFullYear().toString()} 
                                onValueChange={(val) => setSelectedMonth(new Date(parseInt(val), selectedMonth.getMonth(), 1))}
                            >
                                <SelectTrigger className="w-[100px] h-9">
                                    <SelectValue placeholder="Year" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Array.from({ length: 5 }, (_, i) => {
                                        const year = new Date().getFullYear() - i;
                                        return <SelectItem key={year} value={year.toString()}>{year}</SelectItem>;
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <Dialog open={isRegularizeOpen} onOpenChange={setIsRegularizeOpen}>
                        <DialogTrigger asChild>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    disabled={!myConfig?.allow_regularization || (myConfig?.used_regularization_days >= myConfig?.max_regularization_days)}
                                    onClick={() => {
                                        setRegData({
                                            attendance_id: '',
                                            attendance_date: '',
                                            reason: '',
                                            check_in_time: '',
                                            check_out_time: '',
                                        });
                                        setIsRegularizeOpen(true);
                                    }}
                                >
                                    New Request
                                </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[600px]">
                            <DialogHeader>
                                <DialogTitle>Attendance Regularization</DialogTitle>
                                <DialogDescription>
                                    Request a correction for missed or incorrect punches.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleRegularizeSubmit}>
                                <div className="space-y-4 py-4">
                                    <div className="grid grid-cols-1 gap-2">
                                        <Label htmlFor="attendance">Selected Workday</Label>
                                        <select
                                            id="attendance"
                                            className="flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm transition-all focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                            value={regData.attendance_id || regData.attendance_date}
                                            onChange={(e) => {
                                                const rec = regularizableDates.find(h => (h.id && h.id === e.target.value) || (!h.id && h.date === e.target.value));
                                                setRegData({
                                                    ...regData,
                                                    attendance_id: rec?.id || '',
                                                    attendance_date: rec?.id ? '' : rec?.date,
                                                    check_in_time: rec?.check_in ? format(new Date(rec.check_in), "HH:mm") : "09:00",
                                                    check_out_time: rec?.check_out ? format(new Date(rec.check_out), "HH:mm") : "18:00",
                                                });
                                            }}
                                            required
                                        >
                                            <option value="">Choose a date to regularize...</option>
                                            {regularizableDates.map(h => (
                                                <option key={h.id || h.date} value={h.id || h.date}>{h.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="check_in">Correct Check-in Time</Label>
                                            <Input
                                                id="check_in"
                                                type="time"
                                                value={regData.check_in_time}
                                                onChange={(e) => setRegData({ ...regData, check_in_time: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="check_out">Correct Check-out Time</Label>
                                            <Input
                                                id="check_out"
                                                type="time"
                                                value={regData.check_out_time}
                                                onChange={(e) => setRegData({ ...regData, check_out_time: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="reason" className="text-sm font-semibold">Reason for Correction</Label>
                                        <Textarea
                                            id="reason"
                                            className="min-h-[100px] resize-none"
                                            placeholder="Please provide a valid reason for this attendance correction..."
                                            value={regData.reason}
                                            onChange={(e) => setRegData({ ...regData, reason: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                                <DialogFooter className="pt-2">
                                        <Button
                                            type="submit"
                                            className="w-full sm:w-auto px-8"
                                            disabled={actionLoading || (!regData.attendance_id && !regData.attendance_date)}
                                        >
                                            {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Submit Request
                                        </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Check In</TableHead>
                                <TableHead>In Status</TableHead>
                                <TableHead>Check Out</TableHead>
                                <TableHead>Out Status</TableHead>
                                <TableHead>Duration</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="w-[180px]">
                                    <div className="flex flex-col">
                                        <span>Action</span>
                                        {myConfig && (
                                            <span className={`text-[10px] font-normal ${myConfig.used_regularization_days >= myConfig.max_regularization_days ? 'text-red-500' : 'text-muted-foreground'}`}>
                                                Limit: {myConfig.used_regularization_days}/{myConfig.max_regularization_days} used
                                            </span>
                                        )}
                                    </div>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {regularizableDates.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                                        No attendance records found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                regularizableDates.map((record) => {
                                    // Check if this date has an actual history record
                                    const histRecord = history.find(h => h.date === record.date);
                                    
                                    const duration = histRecord?.check_in && histRecord?.check_out
                                        ? Math.round((new Date(histRecord.check_out) - new Date(histRecord.check_in)) / (1000 * 60 * 60) * 10) / 10
                                        : null;

                                    const isRegularizable = record.status === 'LATE' || record.status === 'ABSENT' || record.status === 'MISSING' || (duration && duration < 8);
                                    const hasPendingReg = histRecord?.regularization?.status === 'PENDING';

                                    return (
                                        <TableRow key={record.id || record.date}>
                                            <TableCell className="font-medium">{format(new Date(record.date), 'MMM dd, yyyy')}</TableCell>
                                            <TableCell>{histRecord?.check_in ? format(new Date(histRecord.check_in), 'hh:mm a') : '--'}</TableCell>
                                            <TableCell>
                                                {histRecord?.check_in_status ? (
                                                    <span className={cn(
                                                        "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                                                        histRecord.check_in_status === 'LATE' ? 'bg-red-50 text-red-600 border border-red-100' :
                                                        histRecord.check_in_status === 'EARLY' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                                        'bg-green-50 text-green-600 border border-green-100'
                                                    )}>
                                                        {histRecord.check_in_status.replace('_', ' ')}
                                                    </span>
                                                ) : '--'}
                                            </TableCell>
                                            <TableCell>{histRecord?.check_out ? format(new Date(histRecord.check_out), 'hh:mm a') : '--'}</TableCell>
                                            <TableCell>
                                                {histRecord?.check_out_status ? (
                                                    <span className={cn(
                                                        "px-2 py-0.5 rounded text-[10px] font-bold uppercase",
                                                        histRecord.check_out_status === 'EARLY' ? 'bg-orange-50 text-orange-600 border border-orange-100' :
                                                        histRecord.check_out_status === 'LATE' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                                                        'bg-green-50 text-green-600 border border-green-100'
                                                    )}>
                                                        {histRecord.check_out_status.replace('_', ' ')}
                                                    </span>
                                                ) : '--'}
                                            </TableCell>
                                            <TableCell>{duration ? `${duration} hrs` : '--'}</TableCell>
                                            <TableCell>
                                                <Badge className={statusColors[record.status] || 'bg-gray-100 text-gray-800'}>
                                                    {record.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {histRecord?.regularization ? (
                                                        <Badge variant="outline" className={
                                                            histRecord.regularization.status === 'APPROVED' ? 'text-green-600 border-green-200 bg-green-50' :
                                                                histRecord.regularization.status === 'REJECTED' ? 'text-red-600 border-red-200 bg-red-50' :
                                                                    'text-yellow-600 border-yellow-200 bg-yellow-50'
                                                        }>
                                                            {histRecord.regularization.status}
                                                        </Badge>
                                                    ) : isRegularizable && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="ghost" 
                                                            disabled={!myConfig?.allow_regularization || (myConfig?.used_regularization_days >= myConfig.max_regularization_days)}
                                                            className="h-8 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                            onClick={() => {
                                                                setRegData({
                                                                    attendance_id: record.id || '',
                                                                    attendance_date: record.id ? '' : record.date,
                                                                    reason: '',
                                                                    check_in_time: record.check_in ? format(new Date(record.check_in), "HH:mm") : "09:00",
                                                                    check_out_time: record.check_out ? format(new Date(record.check_out), "HH:mm") : "18:00",
                                                                });
                                                                setIsRegularizeOpen(true);
                                                            }}
                                                        >
                                                            Regularize
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default MyAttendance;
